import { isIndia } from '../../utils/countries';

export const STEP_IDS = {
  IDENTITY: 'identity',
  COMPANY: 'company',
  ADDRESS: 'address',
  BANK: 'bank',
  MSME: 'msme',
  CATEGORIZATION: 'categorization',
  COMPLIANCE: 'compliance',
  AGREEMENTS: 'agreements',
  REVIEW: 'review',
};

const IDENTITY = { id: STEP_IDS.IDENTITY, dataStep: null, label: 'Identity' };
const COMPANY = { id: STEP_IDS.COMPANY, dataStep: 1, label: 'Company Info' };
const ADDRESS = { id: STEP_IDS.ADDRESS, dataStep: 2, label: 'Address' };
const BANK = { id: STEP_IDS.BANK, dataStep: 3, label: 'Bank' };
const MSME = { id: STEP_IDS.MSME, dataStep: 4, label: 'MSME & Turnover' };
const CATEGORIZATION = { id: STEP_IDS.CATEGORIZATION, dataStep: 4, label: 'Categorization' };
const COMPLIANCE = { id: STEP_IDS.COMPLIANCE, dataStep: 5, label: 'Compliance' };
const AGREEMENTS = { id: STEP_IDS.AGREEMENTS, dataStep: 6, label: 'Agreements' };
const REVIEW = { id: STEP_IDS.REVIEW, dataStep: null, label: 'Review' };

/**
 * What a regularization link can ask for, and the steps that hold it. The link opens on just
 * these, so nobody is asked to re-enter what Amber already holds.
 */
const ASK_STEPS = {
  gst: [STEP_IDS.COMPLIANCE],
  pan: [STEP_IDS.COMPLIANCE],
  bank: [STEP_IDS.BANK],
  udyam: [STEP_IDS.CATEGORIZATION],
  documents: [STEP_IDS.COMPANY, STEP_IDS.BANK, STEP_IDS.CATEGORIZATION, STEP_IDS.COMPLIANCE],
};

/** The step ids a set of asks opens; null means the whole form. */
export function stepsForAsks(asks) {
  const keys = (asks || []).filter((k) => k in ASK_STEPS);
  if (keys.length === 0 || (asks || []).includes('full')) return null;
  return new Set(keys.flatMap((k) => ASK_STEPS[k]));
}

/**
 * The single source of truth for step order. UI step N (1-based) is plan[N-1];
 * `dataStep` is the backend session key (step1…step6) the step saves to — null for Identity.
 *
 * registration  : India → Identity, Company, Address, Bank, MSME, Compliance, Agreements, Review (8)
 *                 other → Identity, Company, Address, Bank, Categorization, Agreements, Review (7)
 * regularization: the pre-KYC layout (no Identity; country lives in Company Info)
 *                 India → 6 steps, other → 5 steps; with `asks`, only the steps those asks
 *                 need (a link sent for "verify GST" opens on Compliance alone)
 */
export function getStepPlan(countryOrigin, { mode = 'registration', asks = null } = {}) {
  const india = !countryOrigin || isIndia(countryOrigin);
  if (mode === 'regularization') {
    const full = india
      ? [COMPANY, ADDRESS, BANK, CATEGORIZATION, COMPLIANCE, AGREEMENTS]
      : [COMPANY, ADDRESS, BANK, CATEGORIZATION, AGREEMENTS];
    const wanted = stepsForAsks(asks);
    if (!wanted) return full;
    const narrowed = full.filter((s) => wanted.has(s.id));
    // An ask this layout cannot show (GST for a foreign vendor) falls back to the whole form.
    return narrowed.length > 0 ? narrowed : full;
  }
  return india
    ? [IDENTITY, COMPANY, ADDRESS, BANK, MSME, COMPLIANCE, AGREEMENTS, REVIEW]
    : [IDENTITY, COMPANY, ADDRESS, BANK, CATEGORIZATION, AGREEMENTS, REVIEW];
}

/** 1-based position of a step id in the plan; 0 when the plan does not contain it. */
export function stepIndexOf(plan, id) {
  return plan.findIndex((s) => s.id === id) + 1;
}
